import React, { useMemo, useState } from 'react';
import { BarChart, Bar, LineChart, Line, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertCircle } from 'lucide-react';
import FilterBar from './FilterBar';
import OnboardingFunnel from './OnboardingFunnel';

const COLORS = ['#679632', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const StatCard = ({ title, value, subtitle, color = 'blue' }) => {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    green: 'bg-green-50 text-green-700 border-green-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    gray: 'bg-gray-50 text-gray-700 border-gray-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${colorMap[color]}`}>
      <p className="text-xs font-medium uppercase tracking-wider opacity-70">{title}</p>
      <p className="text-lg font-bold mt-0.5">{value}</p>
      {subtitle && <p className="text-[10px] mt-0.5 opacity-60">{subtitle}</p>}
    </div>
  );
};

const SectionHeader = ({ title, description }) => (
  <div className="mb-4">
    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
  </div>
);

const ChartCard = ({ title, subtitle, children, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 ${className}`}>
    <div className="mb-4">
      <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const tooltipStyle = { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' };

const Analytics = ({ leads, stats, selectedDate, setSelectedDate, dateFrom, setDateFrom, dateTo, setDateTo, selectedBDE, setSelectedBDE, loading, onRefresh, onExportExcel, onExportPDF, showExport = false }) => {
  // Analytics now has two sub-pages: the existing Overview, and a dedicated
  // Onboarding Funnel view that measures average stage durations for onboarded
  // clients (Identified → First Follow-up → Meeting → Onboarded).
  const [subTab, setSubTab] = useState('overview');
  const filteredLeads = useMemo(() => leads || [], [leads]);

  const monthlyTrend = useMemo(() => {
    if (!filteredLeads.length) return [];
    const months = {};
    filteredLeads.forEach(lead => {
      if (!lead.lead_date) return;
      const month = new Date(lead.lead_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      months[month] = (months[month] || 0) + 1;
    });
    return Object.entries(months)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .slice(-6)
      .map(([month, count]) => ({ month, count }));
  }, [filteredLeads]);

  const bdePerformance = useMemo(() => {
    if (!filteredLeads.length) return [];
    const bdeData = {};
    filteredLeads.forEach(lead => {
      const bde = lead.bde_name || 'Unassigned';
      if (!bdeData[bde]) bdeData[bde] = { name: bde, total: 0, closed: 0, inProgress: 0, poc: 0, emails: 0 };
      bdeData[bde].total++;
      bdeData[bde].poc += lead.poc_shortlisted || 0;
      bdeData[bde].emails += lead.email_sent || 0;
      if (lead.stage === 'Onboarded') bdeData[bde].closed++;
      else if (['Contacted', 'Follow-up 1', 'Follow-up 2', 'Proposal', 'Negotiation'].includes(lead.stage)) bdeData[bde].inProgress++;
    });
    return Object.values(bdeData);
  }, [filteredLeads]);

  const stageDistribution = stats?.stageDistribution || [];
  const dailyCounts = stats?.dailyCounts || {};
  const postSalesStats = stats?.postSalesStats || {};
  const postSalesTotal = stats?.postSalesTotal || Object.values(postSalesStats).reduce((sum, s) => sum + (s.total || 0), 0);
  const postSalesDone = Object.values(postSalesStats).reduce((sum, s) => sum + (s.done || 0), 0);
  const postSalesInProgress = Object.values(postSalesStats).reduce((sum, s) => sum + (s.inProgress || 0), 0);
  const postSalesFollowups = Object.values(postSalesStats).reduce((sum, s) => sum + (s.followups || 0), 0);
  const postSalesPending = Object.values(postSalesStats).reduce((sum, s) => sum + (s.pending || 0), 0);
  const postSalesCompletionRate = postSalesTotal > 0 ? ((postSalesDone / postSalesTotal) * 100).toFixed(1) : '0';

  const postSalesBDEPerformance = Object.entries(postSalesStats).map(([name, s]) => ({
    name, total: s.total || 0, done: s.done || 0, inProgress: s.inProgress || 0
  }));

  // Use server-side stage distribution for consistent counts (leads array may be capped by API limit)
  const totalLeadsFromStats = stageDistribution.reduce((sum, s) => sum + (s.value || 0), 0);
  const totalLeadsCount = totalLeadsFromStats || filteredLeads.length;
  const pipelineStages = ['Contacted', 'Follow-up 1', 'Follow-up 2', 'Proposal', 'Negotiation'];
  const pipelineLeads = stageDistribution.filter(s => pipelineStages.includes(s.name)).reduce((sum, s) => sum + (s.value || 0), 0);
  const onboardedDeals = stageDistribution.find(s => s.name === 'Onboarded')?.value || 0;
  const serverConversionRate = totalLeadsCount > 0 ? ((onboardedDeals / totalLeadsCount) * 100).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      {/* Sub-tab switcher */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'onboarding', label: 'Onboarding Funnel' }
        ].map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              subTab === t.id
                ? 'border-[#679632] text-[#679632]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'onboarding' ? (
        <OnboardingFunnel />
      ) : (
      <>
      <FilterBar
        selectedDate={selectedDate} setSelectedDate={setSelectedDate}
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        selectedBDE="All" setSelectedBDE={() => {}}
        showDateRange={true} showBDE={false}
        onExportExcel={onExportExcel} onExportPDF={onExportPDF} showExport={showExport}
      />

      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#679632]" />
          <p className="mt-4 text-gray-500 text-sm">Loading analytics...</p>
        </div>
      ) : stats ? (
        <>
          {/* KPI Cards Row */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <StatCard title="Total Leads" value={totalLeadsCount} color="blue" />
            <StatCard title="In Pipeline" value={pipelineLeads} color="amber" />
            <StatCard title="Onboarded" value={onboardedDeals} color="green" />
            <StatCard title="Conversion" value={`${serverConversionRate}%`} color="purple" />
            <StatCard title="Post Sales" value={postSalesTotal} color="emerald" />
            <StatCard title="Completed" value={`${postSalesCompletionRate}%`} subtitle={`${postSalesDone} done`} color="green" />
          </div>

          {/* Pre Sales Activity Stats */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-center">
              <p className="text-lg font-bold text-gray-900">{Math.max(0, dailyCounts.pocShortlisted || 0)}</p>
              <p className="text-[10px] text-gray-500">POCs Identified</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-center">
              <p className="text-lg font-bold text-gray-900">{Math.max(0, dailyCounts.emailSent || 0)}</p>
              <p className="text-[10px] text-gray-500">Emails Sent</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-center">
              <p className="text-lg font-bold text-gray-900">{Math.max(0, dailyCounts.newConnections || 0)}</p>
              <p className="text-[10px] text-gray-500">New Connections</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-center">
              <p className="text-lg font-bold text-gray-900">{Math.max(0, dailyCounts.autoReplied || 0)}</p>
              <p className="text-[10px] text-gray-500">Auto Replies</p>
            </div>
          </div>

          {/* Charts Row 1: Trends */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Lead Acquisition Trend" subtitle="New leads per month">
              {monthlyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                    <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Line type="monotone" dataKey="count" stroke="#679632" strokeWidth={2.5} name="New Leads" dot={{ fill: '#679632', r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-400 text-sm text-center py-12">No trend data</p>}
            </ChartCard>

            <ChartCard title="Stage Distribution" subtitle="Current pipeline stages">
              {stageDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stageDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" stroke="#9ca3af" style={{ fontSize: '10px' }} angle={-30} textAnchor="end" height={60} />
                    <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="value" name="Leads" radius={[6, 6, 0, 0]}>
                      {stageDistribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-400 text-sm text-center py-12">No stage data</p>}
            </ChartCard>
          </div>

          {/* Charts Row 2: BDE Performance */}
          <SectionHeader title="BDE Performance" description="Team comparison across pre-sales and post-sales" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Pre Sales by BDE" subtitle="Leads, pipeline, and conversions per team member">
              {bdePerformance.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={bdePerformance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                    <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="total" fill="#3b82f6" name="Total" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="inProgress" fill="#f59e0b" name="In Progress" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="closed" fill="#679632" name="Onboarded" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-400 text-sm text-center py-12">No pre-sales data</p>}
            </ChartCard>

            <ChartCard title="Post Sales by BDE" subtitle="Activity and completion per team member">
              {postSalesBDEPerformance.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={postSalesBDEPerformance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                    <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="total" fill="#06b6d4" name="Total" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="inProgress" fill="#f59e0b" name="In Progress" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="done" fill="#679632" name="Completed" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-400 text-sm text-center py-12">No post-sales data</p>}
            </ChartCard>
          </div>

          {/* Post Sales Breakdown */}
          {postSalesTotal > 0 && (
            <>
              <SectionHeader title="Post Sales Overview" description="Current status breakdown" />
              <div className="grid grid-cols-4 gap-2">
                <StatCard title="Done" value={postSalesDone} color="green" />
                <StatCard title="In Progress" value={postSalesInProgress} color="amber" />
                <StatCard title="Follow-ups" value={postSalesFollowups} color="purple" />
                <StatCard title="Pending" value={postSalesPending} color="gray" />
              </div>
            </>
          )}

          {/* BDE Activity Breakdown */}
          {bdePerformance.length > 0 && (
            <>
              <SectionHeader title="BDE Activity Breakdown" description="Detailed metrics per team member" />
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">BDE</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Pipeline</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Onboarded</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">POCs</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Emails</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Conv. Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {bdePerformance.map((bde, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{bde.name}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">{bde.total}</td>
                        <td className="px-4 py-3 text-sm text-center text-amber-600 font-medium">{bde.inProgress}</td>
                        <td className="px-4 py-3 text-sm text-center text-green-600 font-medium">{bde.closed}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">{bde.poc}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">{bde.emails}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            bde.total > 0 && (bde.closed / bde.total) >= 0.1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {bde.total > 0 ? ((bde.closed / bde.total) * 100).toFixed(1) : 0}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
          <AlertCircle size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">No data available for the selected filters</p>
          {onRefresh && (
            <button onClick={onRefresh} className="mt-4 px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] text-sm">
              Refresh Data
            </button>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default Analytics;
